import { useState } from 'react'
import { useAuth } from '../../core/auth'
import { SCOPE_LABELS, SCOPES } from '../../core/caps'
import type { Scope, StaffMember } from '../../core/types'
import { useDirectory, saveStaffMember } from '../admin/model'
import { Text } from '../../ui/Field'
import { Loading, Empty, ErrorState } from '../../ui/States'
import { Phone, MapPin, Pencil } from 'lucide-react'

/**
 * /directory — who works here and how to reach them.
 *
 * Every staff member can read it; only somebody holding admin.staff can change
 * it. That split is the whole reason this is a separate collection from /users:
 * one is a phone book, the other is an access list, and publishing the second
 * to get the first would be a bad trade.
 *
 * Phone numbers are tap-to-call on a phone, which is most of the value — the
 * common case is standing in a hallway needing the front office at the middle
 * school right now.
 */
export function Directory() {
  const { profile, user, can } = useAuth()
  const { rows, err } = useDirectory()
  const [q, setQ] = useState('')
  const [scope, setScope] = useState<Scope | 'all'>('all')
  const [editing, setEditing] = useState<StaffMember | null>(null)

  const mayEdit = can('admin.staff')

  if (err) return <ErrorState body={`Could not load the directory. ${err}`} />
  if (!rows) return <Loading label="Loading the directory" />

  const shown = rows
    .filter((r) => r.active !== false)
    .filter((r) => scope === 'all' || r.buildings?.includes(scope))
    .filter((r) => !q || [r.displayName, r.title, r.email, r.room]
      .filter(Boolean).join(' ').toLowerCase().includes(q.toLowerCase()))

  if (editing) {
    return (
      <Editor
        member={editing}
        who={profile?.displayName ?? user?.email ?? 'unknown'}
        onDone={() => setEditing(null)}
      />
    )
  }

  return (
    <>
      <div className="pagehead">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Staff directory</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '2px 0 0' }}>
            {rows.length} people across the district.
          </p>
        </div>
        {mayEdit && (
          <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={() => setEditing(BLANK)}>
            Add
          </button>
        )}
      </div>

      <input className="opt__input" placeholder="Search by name, title or room" value={q}
        aria-label="Search the directory" onChange={(e) => setQ(e.target.value)}
        style={{ margin: 'var(--s-4) 0 var(--s-2)' }} />

      <div className="chips">
        <button className={`chip${scope === 'all' ? ' is-on' : ''}`} onClick={() => setScope('all')}>
          Everyone
        </button>
        {SCOPES.filter((s) => s !== 'district').map((s) => (
          <button key={s} className={`chip${scope === s ? ' is-on' : ''}`} onClick={() => setScope(s)}>
            {SCOPE_LABELS[s]}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Empty title="Nobody here" body="Try a different building, or clear the search." />
      ) : (
        <ul className="work" style={{ marginTop: 'var(--s-3)' }}>
          {shown.map((r) => (
            <li key={r.email} className="work__row">
              <div className="work__main">
                <div style={{ fontWeight: 610 }}>{r.displayName}</div>
                <div className="work__sub">
                  {[r.title, r.buildings?.map((b) => SCOPE_LABELS[b]).join(', ')].filter(Boolean).join(' · ')}
                </div>
                <div className="dir__contact">
                  {r.phone && (
                    <a href={`tel:${r.phone.replace(/[^\d+]/g, '')}${r.extension ? `,${r.extension}` : ''}`}>
                      <Phone size={13} aria-hidden="true" />
                      {r.phone}{r.extension ? ` ×${r.extension}` : ''}
                    </a>
                  )}
                  {r.room && <span><MapPin size={13} aria-hidden="true" />{r.room}</span>}
                  <a href={`mailto:${r.email}`}>{r.email}</a>
                </div>
              </div>
              {mayEdit && (
                <button className="opt__icon" aria-label={`Edit ${r.displayName}`} onClick={() => setEditing(r)}>
                  <Pencil size={15} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

const BLANK: StaffMember = {
  email: '', displayName: '', title: '', buildings: [], phone: '', extension: '', room: '', active: true,
}

function Editor({ member, who, onDone }: { member: StaffMember; who: string; onDone: () => void }) {
  const [f, setF] = useState<StaffMember>({ ...BLANK, ...member })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const isNew = !member.email
  const set = <K extends keyof StaffMember>(k: K, v: StaffMember[K]) => setF((p) => ({ ...p, [k]: v }))

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try { await saveStaffMember(f, who); onDone() }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false) }
  }

  return (
    <form onSubmit={save}>
      <button type="button" className="backlink" onClick={onDone}
        style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>
        ← Directory
      </button>
      <h1 className="page-title" style={{ margin: '0 0 var(--s-4)' }}>
        {isNew ? 'Add someone' : f.displayName}
      </h1>

      {err && <ErrorState body={`Not saved. ${err}`} />}

      <Text label="Name" value={f.displayName} onChange={(v) => set('displayName', v)} required />
      {/* The email is the key. Changing it would orphan the entry rather than
          rename it, so it is set once. */}
      {isNew
        ? <Text label="District email" type="email" value={f.email} onChange={(v) => set('email', v)} required />
        : <Text label="District email" value={f.email} onChange={() => {}} help="Set when the entry was created." />}
      <Text label="Title" value={f.title} onChange={(v) => set('title', v)}
        placeholder="Social Studies · Head Custodian · Secretary" />
      <Text label="Phone" type="tel" value={f.phone ?? ''} onChange={(v) => set('phone', v)}
        placeholder="515-555-0134" help="Whatever they want listed: desk line or cell." />
      <Text label="Extension" value={f.extension ?? ''} onChange={(v) => set('extension', v)} />
      <Text label="Room" value={f.room ?? ''} onChange={(v) => set('room', v)} placeholder="212" />

      <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s-4)' }}>
        <legend style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)', padding: 0 }}>
          Buildings
        </legend>
        <div style={{ display: 'grid', gap: 4, marginTop: 'var(--s-2)' }}>
          {SCOPES.map((s) => (
            <label key={s} className="pick">
              <input type="checkbox" checked={f.buildings?.includes(s) ?? false}
                onChange={() => set('buildings',
                  f.buildings?.includes(s) ? f.buildings.filter((x) => x !== s) : [...(f.buildings ?? []), s])} />
              <span className="pick__label">{SCOPE_LABELS[s]}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <button className="btn" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      {!isNew && (
        <button type="button" className="btn btn--ghost" style={{ marginTop: 'var(--s-2)' }} disabled={busy}
          onClick={() => { set('active', !f.active) }}>
          {f.active ? 'Mark as no longer here' : 'Mark as here again'}
        </button>
      )}
      <p className="opt__hint">
        Nobody is deleted from the directory. Their name is on courses and records that still have to make sense.
      </p>
    </form>
  )
}
